'use client'

import Image from 'next/image'
import Link from 'next/link'
import { OfferView } from '@/lib/affiliates/trackOfferView'
import { TbDeviceSim, TbShieldCheck, TbMapPin, TbRoad, TbCar, TbMotorbike, TbSailboat, TbBuildingCommunity, TbShieldPlus } from 'react-icons/tb'
import { buildProviderLink } from '@/utils/blog/providerLinkHelpers'
import { declineCityName } from '@/lib/content/declension'
import AffiliateLink from '@/components/common/AffiliateLink'
import GfSectionMark from '@/components/common/GfSectionMark'
import { forReaderLanguage } from '@/lib/affiliates/readerPayment'

/**
 * Иконки для категорий сервисов
 */
const categoryIcons = {
  esim: <TbDeviceSim />,
  vpn: <TbShieldCheck />,
  tours: <TbMapPin />,
  transfer: <TbRoad />,
  carRental: <TbCar />,
  bike: <TbMotorbike />,
  boat: <TbSailboat />,
  stay: <TbBuildingCommunity />,
  insurance: <TbShieldPlus />
}

/**
 * Локализованные названия категорий
 */
const categoryNames = {
  esim: { ru: 'eSIM и мобильный интернет', en: 'eSIM & Mobile Internet' },
  vpn: { ru: 'VPN-сервисы', en: 'VPN Services' },
  tours: { ru: 'Экскурсии и туры', en: 'Tours & Excursions' },
  transfer: { ru: 'Трансферы', en: 'Transfers' },
  carRental: { ru: 'Аренда авто', en: 'Car Rental' },
  bike: { ru: 'Велосипеды и скутеры', en: 'Bikes & Scooters' },
  boat: { ru: 'Лодки и яхты', en: 'Boats & Yachts' },
  stay: { ru: 'Отели и апартаменты', en: 'Hotels & Apartments' },
  insurance: { ru: 'Страхование', en: 'Travel Insurance' }
}

/**
 * Карточка провайдера (GF)
 */
function ProviderCard({ provider, language, vertical, countrySlug, pageSlug }) {
  if (!provider || provider._type === 'service') return null

  const title = provider.title || provider.name || 'Сервис'
  const description = language === 'ru'
    ? (provider.descriptionRu || provider.descriptionEn)
    : (provider.descriptionEn || provider.descriptionRu)

  // benefits в Sanity могут быть как простыми строками, так и объектами
  const rawBenefits = language === 'ru'
    ? (provider.benefitsRu || provider.benefitsEn || [])
    : (provider.benefitsEn || provider.benefitsRu || [])

  const benefits = (rawBenefits || [])
    .map((item) => {
      if (!item) return null
      if (typeof item === 'string') return item
      // Все провайдеры хранят преимущества объектами {title, description: блоки}: берём title,
      // как в карточке-оффере — без этого чипы в подвале не выводились ни у кого (ревью 30.09.2026)
      if (typeof item === 'object' && typeof item.title === 'string' && item.title.trim()) {
        return item.title.trim()
      }
      if (typeof item === 'object' && typeof item.description === 'string') {
        return item.description
      }
      if (Array.isArray(item.children)) {
        return item.children
          .map((child) => (typeof child.text === 'string' ? child.text : ''))
          .join(' ')
          .trim()
      }
      return null
    })
    .filter((text) => typeof text === 'string' && text.trim().length > 0)

  const affiliateUrl = buildProviderLink(provider, language, countrySlug, {
    pageSlug,
    position: 'blog-footer',
  })

  return (
    <AffiliateLink
      href={affiliateUrl}
      provider={provider.code}
      vertical={vertical}
      country={countrySlug}
      position="blog-footer"
      label={title}
      className="gf-svc-card"
    >
      {provider.logoUrl && (
        <div className="gf-svc-logo">
          <Image
            src={provider.logoUrl}
            alt={provider.logoAlt || title}
            width={40}
            height={40}
            className="object-contain"
          />
        </div>
      )}
      <div className="gf-svc-card-bd">
        <h4>{title}</h4>
        {description && <p className="ex">{description}</p>}
        {benefits.length > 0 && (
          <div className="gf-svc-benefits">
            {benefits.slice(0, 1).map((benefit, idx) => (
              <span key={idx}>{benefit}</span>
            ))}
          </div>
        )}
        {/* карточка кликабельна целиком, но не выглядела кнопкой: 16 плашек без повода кликнуть */}
        <span className="gf-svc-go">{language === 'ru' ? 'На сайт' : 'Visit'} →</span>
      </div>
    </AffiliateLink>
  )
}

// Вертикал карточки → код сервис-страницы (/<country>/<code>). transfer/carRental/
// bike/boat ведут на общую страницу транспорта.
const SERVICE_CODE = {
  esim: 'esim', vpn: 'vpn', tours: 'tours', stay: 'stay', insurance: 'insurance',
  transfer: 'transport', carRental: 'transport', bike: 'transport', boat: 'transport',
}

/**
 * Секция категории сервисов (GF)
 */
function CategorySection({ categoryKey, items, language, countrySlug, pageSlug }) {
  const providers = forReaderLanguage(items?.filter(item => item._type === 'affiliateProvider' && item.active === true) || [], language)
  const services = items?.filter(item => item._type === 'service') || []

  if (providers.length === 0) return null

  const icon = categoryIcons[categoryKey]
  const name = categoryNames[categoryKey]?.[language] || categoryKey

  // Ссылка на хаб сервиса: из service-дока, иначе из страны пресета + кода вертикала.
  // Гарантирует контекстную внутреннюю ссылку на money-страницу для каждой категории.
  const service = services[0]
  const serviceCountry = service?.country?.slug || countrySlug
  const serviceCode = service?.code || SERVICE_CODE[categoryKey]
  const serviceUrl = serviceCountry && serviceCode
    ? `/${language}/${serviceCountry}/${serviceCode}`
    : null

  return (
    <div className="gf-svc-cat">
      <div className="gf-svc-cat-head">
        <div className="gf-svc-cat-title">
          <span className="ic">{icon}</span>
          <h3>{name}</h3>
        </div>
        {serviceUrl && (
          <Link href={serviceUrl} className="gf-svc-more">
            {language === 'ru' ? 'Подробнее' : 'Learn more'} →
          </Link>
        )}
      </div>

      <div className={`gf-svc-grid ${providers.length === 1 ? 'one' : ''}`}>
        {providers.map((provider) => (
          <ProviderCard
            key={provider._id}
            provider={provider}
            language={language}
            vertical={categoryKey}
            countrySlug={countrySlug}
            pageSlug={pageSlug}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Главный компонент блока рекомендуемых сервисов (GF)
 * @param {Object} props
 * @param {Object} props.preset - Данные пресета из Sanity
 * @param {string} props.language - Язык ('ru' или 'en')
 */
export default function RecommendedServicesBlock({ preset, language = 'ru', pageSlug = 'unknown-page' }) {
  if (!preset) return null

  // Порядок по деньгам: аренда — 76% дохода и ~$9 за клик против $0.2–0.3 у остальных, а стояла
  // пятой, после eSIM и VPN — на мобильном Localrent оказывался на 21-м экране из 26 (ревью
  // 30.09.2026). Мобильные клики подвала за 90 дней: carRental 5, transfer 3, stay 2, vpn 0.
  const categories = [
    { key: 'carRental', items: preset.carRentalItems },
    { key: 'transfer', items: preset.transferItems },
    { key: 'stay', items: preset.stayItems },
    { key: 'tours', items: preset.toursItems },
    { key: 'bike', items: preset.bikeItems },
    { key: 'esim', items: preset.esimItems },
    { key: 'insurance', items: preset.insuranceItems },
    { key: 'vpn', items: preset.vpnItems },
    { key: 'boat', items: preset.boatItems }
  ].filter(cat => cat.items && cat.items.length > 0)

  if (categories.length === 0) return null

  const sectionTitle = language === 'ru' ? 'Рекомендуемые сервисы' : 'Recommended Services'

  const countrySlug = preset.country?.slug || null
  const countryNameRu = preset.country?.nameRu || ''
  const countryNameEn = preset.country?.nameEn || preset.country?.name || ''
  const declinedCountryName = countryNameRu ? declineCityName(countryNameRu, 'accusative') : ''
  const subtitle = language === 'ru'
    ? `Полезные сервисы для путешествия${declinedCountryName ? ` в ${declinedCountryName}` : ''}`
    : `Useful services for your trip${countryNameEn ? ` to ${countryNameEn}` : ''}`

  // Контекстная ссылка на хаб страны — сейчас на него ведут только boilerplate-ссылки
  // из меню/футера (Google их дисконтирует). Редакционная ссылка из каждого поста
  // усиливает хаб → он каскадом раздаёт вес на сервисы и города.
  const hubName = language === 'ru' ? (declinedCountryName || countryNameRu) : countryNameEn
  const hubLabel = language === 'ru'
    ? `Всё для поездки в ${hubName}`
    : `Everything for your trip to ${hubName}`

  return (
    <OfferView as="section" className="gf-svc" country={countrySlug} position="blog-footer">
      <GfSectionMark eyebrow={sectionTitle} title={subtitle} />
      <div className="gf-svc-cats">
        {categories.map((category) => (
          <CategorySection
            key={category.key}
            categoryKey={category.key}
            items={category.items}
            language={language}
            countrySlug={countrySlug}
            pageSlug={pageSlug}
          />
        ))}
      </div>
      {countrySlug && hubName && (
        <div className="gf-svc-hub">
          <Link href={`/${language}/${countrySlug}`} className="gf-alllink">{hubLabel}</Link>
        </div>
      )}
    </OfferView>
  )
}
